import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Wallet, Calculator, ScrollText, IndianRupee, Settings2, Plus, CheckCircle2, User, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PageHeader = ({ title, subtitle, icon: Icon }) => (
  <div className="flex items-start gap-3">
    {Icon && <div className="h-10 w-10 rounded-full bg-[#0F5E39] text-white flex items-center justify-center"><Icon size={18}/></div>}
    <div>
      <div className="font-serif text-2xl">{title}</div>
      {subtitle && <div className="text-xs text-[#4A5A50]">{subtitle}</div>}
    </div>
  </div>
);

export default function HRPayrollPage() {
  const [tab, setTab] = useState("structures");
  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <PageHeader
        title="HR · Payroll"
        subtitle="Salary structures, statutory settings, advances/loans, monthly payroll runs"
        icon={Wallet}
      />
      <div className="inline-flex rounded-full bg-white border border-[#E4DFCF] p-1 text-sm" data-testid="hr-tabs">
        {[
          { k: "structures", label: "Structures", icon: IndianRupee },
          { k: "advances",   label: "Advances",   icon: Plus },
          { k: "payroll",    label: "Payroll runs",icon: Calculator },
          { k: "settings",   label: "Settings",   icon: Settings2 },
          { k: "mine",       label: "My payslips",icon: ScrollText },
        ].map(({ k, label, icon: Icon }) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${tab === k ? "bg-[#0F5E39] text-white" : "text-[#4A5A50]"}`}
            data-testid={`hr-tab-${k}`}>
            <Icon size={14}/> {label}
          </button>
        ))}
      </div>

      {tab === "structures" && <StructuresPanel/>}
      {tab === "advances"   && <AdvancesPanel/>}
      {tab === "payroll"    && <PayrollPanel/>}
      {tab === "settings"   && <SettingsPanel/>}
      {tab === "mine"       && <MyPayslipsPanel/>}
    </div>
  );
}


function useEmployees() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/field-sales/team/salespeople");
        setTeam(Array.isArray(data) ? data : (data.items || []));
      } catch (_) { /* noop */ }
    })();
  }, []);
  return team;
}


function StructuresPanel() {
  const team = useEmployees();
  const [rows, setRows] = useState([]);
  const [eid, setEid] = useState("");
  const [basic, setBasic] = useState("");
  const [hra, setHra] = useState("");
  const [sa, setSa] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/hr/structures");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!eid || !basic) return toast.warning("Employee + basic required");
    setBusy(true);
    try {
      await api.post("/hr/structures", {
        employee_id: eid, basic: Number(basic),
        hra: Number(hra) || 0, special_allowance: Number(sa) || 0,
      });
      setBasic(""); setHra(""); setSa("");
      toast.success("Salary structure saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="structures-panel">
      <Card><CardContent className="p-4 space-y-3">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <Label>Employee</Label>
            <select value={eid} onChange={(e) => setEid(e.target.value)} className="w-full h-10 rounded-md border border-[#E4DFCF] px-3 bg-white" data-testid="struct-emp-select">
              <option value="">— select —</option>
              {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div><Label>Basic ₹</Label><Input type="number" value={basic} onChange={(e) => setBasic(e.target.value)} data-testid="struct-basic-input"/></div>
          <div><Label>HRA ₹</Label><Input type="number" value={hra} onChange={(e) => setHra(e.target.value)}/></div>
          <div><Label>Special ₹</Label><Input type="number" value={sa} onChange={(e) => setSa(e.target.value)}/></div>
        </div>
        <div className="flex justify-end">
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={save} data-testid="struct-save-btn">Save structure</Button>
        </div>
      </CardContent></Card>

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-[#4A5A50] italic">No structures yet.</p>}
        {rows.map((s) => {
          const emp = team.find((u) => u.id === s.employee_id);
          return (
            <Card key={s.id}><CardContent className="p-4 flex items-center gap-3" data-testid={`struct-row-${s.employee_id}`}>
              <User size={18} className="text-[#C9A24B]"/>
              <div className="flex-1">
                <div className="font-serif">{emp?.name || s.employee_id.slice(0, 8)}</div>
                <div className="text-xs text-[#4A5A50]">Basic ₹{s.basic.toLocaleString("en-IN")} · HRA ₹{s.hra} · Special ₹{s.special_allowance}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Monthly CTC</div>
                <div className="font-serif text-lg text-[#0F5E39]">₹{s.ctc_monthly.toLocaleString("en-IN")}</div>
              </div>
            </CardContent></Card>
          );
        })}
      </div>
    </div>
  );
}


function AdvancesPanel() {
  const team = useEmployees();
  const [rows, setRows] = useState([]);
  const [eid, setEid] = useState("");
  const [amt, setAmt] = useState("");
  const [emi, setEmi] = useState("");
  const [kind, setKind] = useState("advance");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/hr/advances");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!eid || !amt) return toast.warning("Employee + amount required");
    setBusy(true);
    try {
      await api.post("/hr/advances", {
        employee_id: eid, amount: Number(amt),
        emi_amount: Number(emi) || 0, kind,
      });
      setAmt(""); setEmi("");
      toast.success("Advance created");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="advances-panel">
      <Card><CardContent className="p-4 space-y-3">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <Label>Employee</Label>
            <select value={eid} onChange={(e) => setEid(e.target.value)} className="w-full h-10 rounded-md border border-[#E4DFCF] px-3 bg-white" data-testid="adv-emp-select">
              <option value="">— select —</option>
              {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div><Label>Amount ₹</Label><Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="adv-amt-input"/></div>
          <div><Label>Monthly EMI ₹</Label><Input type="number" value={emi} onChange={(e) => setEmi(e.target.value)}/></div>
          <div>
            <Label>Kind</Label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full h-10 rounded-md border border-[#E4DFCF] px-3 bg-white">
              <option value="advance">Advance</option>
              <option value="loan">Loan</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={create} data-testid="adv-create-btn">Create</Button>
        </div>
      </CardContent></Card>

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-[#4A5A50] italic">No advances yet.</p>}
        {rows.map((a) => {
          const emp = team.find((u) => u.id === a.employee_id);
          return (
            <Card key={a.id}><CardContent className="p-4 flex items-center gap-3">
              <IndianRupee size={18} className="text-[#C9A24B]"/>
              <div className="flex-1">
                <div className="font-serif">{emp?.name || a.employee_id.slice(0, 8)} · {a.kind}</div>
                <div className="text-xs text-[#4A5A50]">₹{a.amount} · EMI ₹{a.emi_amount || 0}/mo · outstanding ₹{a.outstanding}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${a.status === "settled" ? "bg-[#E1F0E6] text-[#0F5E39]" : "bg-[#FFF6E1] text-[#8A6600]"}`}>{a.status}</span>
            </CardContent></Card>
          );
        })}
      </div>
    </div>
  );
}


function PayrollPanel() {
  const [runs, setRuns] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/hr/payroll/runs");
    setRuns(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hr/payroll/run", { period_month: period, days_in_period: Number(days) || 30 });
      toast.success(`Generated ${data.employee_count} payslips`);
      setDetail(data);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Run failed"); }
    finally { setBusy(false); }
  };

  const openRun = async (id) => {
    const { data } = await api.get(`/hr/payroll/runs/${id}`);
    setDetail(data);
  };

  const finalise = async (id) => {
    if (!window.confirm("Finalise this payroll run? Advances will be deducted.")) return;
    await api.post(`/hr/payroll/runs/${id}/finalise`);
    toast.success("Finalised");
    load();
    openRun(id);
  };

  return (
    <div className="space-y-4" data-testid="payroll-panel">
      <Card><CardContent className="p-4 space-y-3">
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div><Label>Period</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="payroll-period-input"/></div>
          <div><Label>Days in period</Label><Input type="number" value={days} onChange={(e) => setDays(e.target.value)}/></div>
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={run} data-testid="payroll-run-btn"><Calculator size={14} className="mr-1"/>Generate</Button>
        </div>
      </CardContent></Card>

      <div className="space-y-2">
        {runs.map((r) => (
          <Card key={r.id}><CardContent className="p-4 flex items-center gap-3" data-testid={`payroll-run-row-${r.id}`}>
            <CalendarClock size={18} className="text-[#C9A24B]"/>
            <div className="flex-1">
              <div className="font-serif text-lg">{r.period_month} · {r.employee_count} employees</div>
              <div className="text-xs text-[#4A5A50]">Gross ₹{r.total_gross?.toLocaleString("en-IN")} · Net ₹{r.total_net?.toLocaleString("en-IN")} · Deductions ₹{r.total_deductions?.toLocaleString("en-IN")}</div>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${r.status === "finalised" || r.status === "paid" ? "bg-[#E1F0E6] text-[#0F5E39]" : "bg-[#FFF6E1] text-[#8A6600]"}`}>{r.status}</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => openRun(r.id)}>Open</Button>
            {r.status === "draft" && <Button size="sm" className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => finalise(r.id)}><CheckCircle2 size={13} className="mr-1"/>Finalise</Button>}
          </CardContent></Card>
        ))}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="payroll-detail-modal">
            <div className="px-4 py-3 bg-[#083F22] text-white flex items-center gap-2">
              <div className="font-serif text-lg">Payroll · {detail.period_month}</div>
              <span className="ml-auto text-xs uppercase tracking-widest">{detail.status}</span>
              <Button size="sm" variant="outline" className="border-white/40 text-white bg-white/10 hover:bg-white/20" onClick={() => setDetail(null)}>Close</Button>
            </div>
            <div className="overflow-auto p-3">
              <table className="w-full text-xs">
                <thead className="bg-[#F6F1E1] text-[#4A5A50] uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="p-2 text-left">Employee</th>
                    <th className="p-2 text-right">Basic</th>
                    <th className="p-2 text-right">Gross</th>
                    <th className="p-2 text-right">PF</th>
                    <th className="p-2 text-right">ESIC</th>
                    <th className="p-2 text-right">TDS</th>
                    <th className="p-2 text-right">PT</th>
                    <th className="p-2 text-right">Adv</th>
                    <th className="p-2 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.payslips || []).map((p) => (
                    <tr key={p.id} className="border-b border-[#E4DFCF]">
                      <td className="p-2">{p.employee_name || p.employee_id.slice(0, 8)}</td>
                      <td className="p-2 text-right">₹{p.basic.toLocaleString("en-IN")}</td>
                      <td className="p-2 text-right font-medium">₹{p.gross.toLocaleString("en-IN")}</td>
                      <td className="p-2 text-right text-red-700">-{p.pf_employee}</td>
                      <td className="p-2 text-right text-red-700">-{p.esic_employee}</td>
                      <td className="p-2 text-right text-red-700">-{p.tds}</td>
                      <td className="p-2 text-right text-red-700">-{p.professional_tax}</td>
                      <td className="p-2 text-right text-red-700">-{p.advance_deduction}</td>
                      <td className="p-2 text-right font-serif text-[#0F5E39]">₹{p.net_pay.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/hr/settings"); setS(data); })(); }, []);
  const save = async () => {
    try {
      await api.patch("/hr/settings", s);
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
  };
  if (!s) return <div className="text-sm text-[#4A5A50]">Loading…</div>;
  return (
    <Card><CardContent className="p-4 space-y-4" data-testid="hr-settings-panel">
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label>PF employee %</Label><Input type="number" step="0.01" value={s.pf_employee_pct} onChange={(e) => setS({ ...s, pf_employee_pct: Number(e.target.value) })}/></div>
        <div><Label>PF employer %</Label><Input type="number" step="0.01" value={s.pf_employer_pct} onChange={(e) => setS({ ...s, pf_employer_pct: Number(e.target.value) })}/></div>
        <div><Label>PF wage ceiling ₹</Label><Input type="number" value={s.pf_wage_ceiling} onChange={(e) => setS({ ...s, pf_wage_ceiling: Number(e.target.value) })}/></div>
        <div><Label>ESIC employee %</Label><Input type="number" step="0.01" value={s.esic_employee_pct} onChange={(e) => setS({ ...s, esic_employee_pct: Number(e.target.value) })}/></div>
        <div><Label>ESIC employer %</Label><Input type="number" step="0.01" value={s.esic_employer_pct} onChange={(e) => setS({ ...s, esic_employer_pct: Number(e.target.value) })}/></div>
        <div><Label>ESIC ceiling ₹</Label><Input type="number" value={s.esic_wage_ceiling} onChange={(e) => setS({ ...s, esic_wage_ceiling: Number(e.target.value) })}/></div>
        <div><Label>Professional tax / mo</Label><Input type="number" value={s.professional_tax_monthly} onChange={(e) => setS({ ...s, professional_tax_monthly: Number(e.target.value) })}/></div>
      </div>
      <div>
        <Label>Income-tax slabs (JSON)</Label>
        <Textarea rows={7}
          value={JSON.stringify(s.tds_slabs, null, 2)}
          onChange={(e) => {
            try { setS({ ...s, tds_slabs: JSON.parse(e.target.value) }); } catch (_) {}
          }}
          className="font-mono text-xs" data-testid="hr-slabs-input"/>
        <p className="text-[11px] text-[#4A5A50] mt-1">Progressive slabs, in ₹. `upto: 0` means "and above" — must be last.</p>
      </div>
      <div className="flex justify-end">
        <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={save} data-testid="hr-settings-save-btn">Save settings</Button>
      </div>
    </CardContent></Card>
  );
}


function MyPayslipsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/hr/payslips/mine"); setRows(data.items || []); } catch (_) {} })(); }, []);
  return (
    <div className="space-y-2" data-testid="my-payslips-panel">
      {rows.length === 0 && <p className="text-sm text-[#4A5A50] italic">No finalised payslips yet.</p>}
      {rows.map((r, i) => (
        <Card key={i}><CardContent className="p-4 flex items-center gap-3">
          <ScrollText size={18} className="text-[#C9A24B]"/>
          <div className="flex-1">
            <div className="font-serif">{r.period_month} · {r.status}</div>
            <div className="text-xs text-[#4A5A50]">
              Gross ₹{r.payslip.gross.toLocaleString("en-IN")} · Deductions ₹{(r.payslip.pf_employee + r.payslip.esic_employee + r.payslip.tds + r.payslip.professional_tax + r.payslip.advance_deduction).toFixed(2)}
            </div>
          </div>
          <div className="font-serif text-xl text-[#0F5E39]">₹{r.payslip.net_pay.toLocaleString("en-IN")}</div>
        </CardContent></Card>
      ))}
    </div>
  );
}
